/**
 * @file App-owned parent-board adapter registry.
 *
 * Shared board modules stay generic and import-free from domain modules. This
 * registry is the application composition point that maps metadata adapter
 * keys from `shared/board/boardRegistry.js` to concrete domain adapters.
 */

import { showDetails } from "../routing/navigationController.js";
import { quickCloseTask } from "../../shared/api/checklists.js";
import { getBoardRegistryEntry } from "../../shared/board/boardRegistry.js";
import { renderFooterToggle } from "../../shared/board/boardFooterToggle.js";
import { openParentForm } from "../../shared/forms/parentFormModal.js";
import { taskBoardAdapter } from "../../domains/tasks/taskBoardAdapter.js";
import { patientBoardAdapter } from "../../domains/patients/patientBoardAdapter.js";
import { projectBoardAdapter } from "../../domains/projects/projectBoardAdapter.js";
import { assetBoardAdapter } from "../../domains/assets/assetBoardAdapter.js";
import { labsBoardAdapter } from "../../domains/labs/labsBoardAdapter.js";
import { maintenanceBoardAdapter } from "../../domains/maintenance/maintenanceBoardAdapter.js";

const SHARED_BOARD_ACTION_ADAPTERS = Object.freeze({
  renderFooterToggle,
  openDetails: showDetails,
  openTaskForm: (taskDefaults) => openParentForm("task", taskDefaults),
  onQuickCloseTask: quickCloseTask,
});

const PARENT_BOARD_ADAPTERS_BY_KEY = Object.freeze({
  task: taskBoardAdapter,
  patient: patientBoardAdapter,
  project: projectBoardAdapter,
  asset: assetBoardAdapter,
  lab: labsBoardAdapter,
  maintenance: maintenanceBoardAdapter,
});

function getAdapterKeyForView(viewKey) {
  const board = getBoardRegistryEntry(viewKey);
  return board?.adapters?.cardAdapterKey || null;
}

/**
 * Returns the concrete parent-board adapter for a registered view.
 * @param {string} viewKey - Parent-board view key.
 * @returns {import("../../shared/board/parentBoardAdapterFactory.js").ParentBoardAdapter|null} Concrete adapter, or null when unmapped.
 */
export function getParentBoardAdapter(viewKey) {
  const adapterKey = getAdapterKeyForView(viewKey);
  return adapterKey ? PARENT_BOARD_ADAPTERS_BY_KEY[adapterKey] || null : null;
}

/**
 * Returns the render-dispatch adapter object for a parent-board view.
 * @param {string} viewKey - Parent-board view key.
 * @returns {object|null} Dispatch adapters, or null when unmapped.
 */
export function getParentBoardViewAdapters(viewKey) {
  const adapter = getParentBoardAdapter(viewKey);
  if (!adapter) {
    return null;
  }

  return {
    ...SHARED_BOARD_ACTION_ADAPTERS,
    renderers: adapter.renderers,
  };
}

/**
 * Returns all currently registered parent-board adapters keyed by adapter key.
 * @returns {Object<string, import("../../shared/board/parentBoardAdapterFactory.js").ParentBoardAdapter>} Adapter registry snapshot.
 */
export function getParentBoardAdaptersByKey() {
  return PARENT_BOARD_ADAPTERS_BY_KEY;
}
